<template>
    <div class="flex-container">
        <span class="block mb-3 font-bold text-sm" style='color:#4C5669'>Rechercher un produit</span>
        <div class="flex-item relative">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3">
            <i class="pi pi-search text-gray-500"></i>
            </span>
            <InputText
                type="text"
                v-model="criteria"
                placeholder="Recherche"
                @keyup.enter="onSearchInputDt()"
                class="pl-10 w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
        </div>
    </div>
  <FormKit
    type="form"
    :form-class="isFormSubmitted ? 'hide' : 'show'"
    @submit="submitHandler"
    :actions="false"
    #default="{ state: { valid } }"
  >
    <div class="card">
      <DataTable lazy
                 dataKey="uuid"
                 stateStorage="local"
                 ref="dt"
                 :state-key="tableLocalStorageKey"
                 :value="tableData"
                 :paginator="true"
                 :rows="rows"
                 :rowHover="true"
                 removableSort
                 v-model:selection="selectedProducts"
                 :loading="isLoading"
                 :totalRecords="totalRecords"
                 :currentPage="currentPage"
                 :selectAll="isSelectAllBtnChecked"
                 paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                 :rowsPerPageOptions="rowsPerPageOptions"
                 currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
                 @select-all-change="onRowSelectAll($event)"
                 @row-select="onRowSelect($event)"
                 @row-unselect="onRowUnselect($event)"
                 @page="onPaginate($event)">
        <template #header>
          <div class="flex flex-row flex-nowrap card-header border-0">
            <div class="card-toolbar w-full">
              <FormKit
                  type="autocomplete"
                  name="autocomplete"
                  label="Rechercher une catégorie"
                  placeholder="Example: E-billet"
                  v-model="selectedOptionsCategory"
                  :options="searchCategories"
                  selection-appearance="option"
                  multiple
              >
                  <!--SELECTION SLOT-->
                  <template #selection="{ option }">
                      <div class="formkit-selection">
                          <div class="formkit-option">
                              <span>{{ option.label }}</span>
                          </div>
                      </div>
                  </template>
                  <!--/SELECTION SLOT-->
              </FormKit>
            </div>
              
            <div class="card-toolbar w-full">
              <div class="flex flex-col justify-center">
                <p>Nombre total de produits : {{ totalRecords }}</p>
                <p>Nombre de produit déjà sélectionnée(s) : {{ totalSelectedVariantProductsInDatabase }} produit(s)</p>
                <p>Nombre de produit à rajouter : {{ totalSelectedProductsToAdd }}</p>
                <p>Nombre de produit à supprimer : {{ totalUnSelectedProducts }}</p>
                <p v-show="isInFilteredView">Vous êtes en vue filtrée</p>
              </div>
            </div>
            
          </div>
        </template>
        <div class="card-body pt-0">
          <Column selectionMode="multiple"></Column>
          <Column field="name" header="Produit">
            <template #body="slotProps">
              <span> {{ slotProps.data.name }}</span>
            </template>
          </Column>
          <Column field="category" header="Catégorie">
            <template #body="slotProps">
              <span v-if="slotProps.data.categories?.length > 0" :title="formatCategoriesProduct(slotProps.data.categories)">
                  {{ slotProps.data.categories[0].value }}
              </span>
            </template>
          </Column>
          <Column field="price" header="Prix">
            <template #body="slotProps">
              <span> {{ slotProps.data.priceFormatted }} </span>
            </template>
          </Column>
        </div>
        <template #empty>Aucun résultat trouvé.</template>
      </DataTable>
    </div>
    <div class="ml-96 mt-10">
      <FormKit type="text"
               name="name"
               label="Nom de la collection*"
               validation="required"
               v-model="collectionName"
               :validation-messages="{ required: 'Ce champ est obligatoire.' }"
      />
      <FormKit type="submit" label="Enregistrer" :disabled="!valid || isLoading"/>
    </div>
  </FormKit>
</template>

<script lang="ts" setup>
import {computed, onBeforeMount, onMounted, ref, watch} from "vue";
import swal from "@/_helpers/swal";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import {fetchProductsForGrant, formatCategoriesProduct} from "@/_services/product/variant/productVariant";
import {getVariantProductCollection, updateVariantProductCollection} from "@/_services/grant/variantProductCollection";
import router from "@/router";
import {fetchCategoriesForDropDown} from "@/_services/category/category";
import {useRoute} from "vue-router";
import {
    IEditProductVariantToCollectionPlayload,
    IProductVariantForGrant
} from "@/models/product/variant/ProductVariantForGrant";
const tableLocalStorageKey = "tableSelectedProductsToAddInCollection";
const selectedProductToAddStorageKey = "editSelectedVariantProductsToAddInCollection";
const selectedProductToRemoveStorageKey = "editSelectedVariantProductsToRemoveFromCollection";
const selectAllBtnStatusStorageKey = "editSelectAllVariantProductsForCollectionBtnStatus";
const dbProductsOfCollectionInStorage = "editDbProductsOfCollectionInStorage";
const totalSelectedProductsToAdd = ref<number>(0);
const totalUnSelectedProducts = ref<number>(0);
const refItemsPerPage = ref<number>(0);
const productsOfCurrentPage = ref(Array<IProductVariantForGrant>());
const totalSelectedVariantProductsInDatabase = ref<number>(0);
const isFormSubmitted = ref(false);
const isLoading = ref<boolean>(true);
const vProducts = Array<IProductVariantForGrant>();
const tableData = ref<Array<IProductVariantForGrant>>(vProducts);
const selectedProducts = ref(Array<IProductVariantForGrant>());
const currentPage = ref<number>(0);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const criteria = ref<string>("");
const variantProductCollectionUuid = ref<string>("");
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const selectedOptionsCategory = ref([]);
const route = useRoute();
const collectionName = ref<string>("");
const isSelectAllBtnChecked = ref(false);

onBeforeMount(() => {
    initLocalStorageData();
});

onMounted( async () => {
    variantProductCollectionUuid.value = route.params.uuid as string;
    await getVariantProductCollection(variantProductCollectionUuid.value).then((apiResult) => {
        collectionName.value = apiResult.data.data.name;
    });
    await populateDataTableWithLocalStorageFilters();
});

watch(selectedOptionsCategory, () => {
    onChangeSelectedCategory();
});

watch(isSelectAllBtnChecked, () => {
    setIsSelectedAllBtnStatusLocalStorage(isSelectAllBtnChecked.value);
});

const onChangeSelectedCategory = async (): Promise<void> => {
    showSpinner();
    const search = criteria.value.trim();
    await populateDatatable(rows.value, page.value, "" != search ? search : "", selectedOptionsCategory.value);
};

const isInFilteredView = computed(() => {
    return selectedOptionsCategory.value.length > 0 || totalSelectedProductsToAdd.value > 0 || totalUnSelectedProducts.value > 0;
});

async function searchCategories({ search, page, hasNextPage }) {
    const [totalCategoriesRecordsForPickListApi, categoriesForPickListApi] = await fetchCategoriesForDropDown(5, page, search);
    const hasItems = categoriesForPickListApi.length > 0;
    if (hasItems && page !== totalCategoriesRecordsForPickListApi) {
        hasNextPage();
    }

    return categoriesForPickListApi;
}

const submitHandler = async (data) => {
    swal
      .confirm("Êtes-vous sur de vouloir éditer cette collection ?")
      .then((result) => {
        if (result.isConfirmed) {
          showSpinner();
            const selectedProductsToAdd = getSelectedProductsToAddFromStorage();
            const unSelectedProductsToRemove = getUnSelectedProductsToRemoveFromStorage();
            const params: IEditProductVariantToCollectionPlayload = {
                uuid: variantProductCollectionUuid.value,
                name: data.name,
                selectedProductsUuidsToAdd: selectedProductsToAdd,
                unSelectedProductsUuidsToRemove: unSelectedProductsToRemove
            };
            updateVariantProductCollection(params).then(() => {
            swal.success(
                "Editer !", 
                "Vous venez de éditer une collection, la mise à jour de la catégorie " +
                "sera visible sur les subventions dans une heure").then(() => {
              router.push({ name: 'listVariantProductCollection'});
            });
          });
        }
      });
  
    isFormSubmitted.value = true
}

function onSearchInputDt() {
  let search = criteria.value.trim();
  if (search.length >= 0) {
    showSpinner();
    populateDatatable(rows.value, page.value, "" != search ? search : "", selectedOptionsCategory.value);
  }
}

const populateDatatable = async (itemsPerPage: number, page: number, criteria?: string | undefined, categoriesFilter?: string[] | undefined) => {
  const [loadingDt, totalRecordsDt, tableDataDt] = await fetchProductsForGrant(
    itemsPerPage,
    page,
    criteria,
    categoriesFilter,
    variantProductCollectionUuid.value
  );
  totalRecords.value = totalRecordsDt;
  isLoading.value = loadingDt;
  tableData.value = tableDataDt;
  refItemsPerPage.value = itemsPerPage;
  productsOfCurrentPage.value = tableData.value;
  totalSelectedVariantProductsInDatabase.value = productsOfCurrentPage.value[0]?.totalSelectedVariantProducts;
  const selectedProductsUuidsInDatabaseOnCurrentPage = productsOfCurrentPage.value
      .filter(item => item.isVariantProductInCollection === true)
      .map(item => item.uuid);
  addDbProductsToCollectionInStorage(selectedProductsUuidsInDatabaseOnCurrentPage);
  let selectedProductsOnCurrentPage: IProductVariantForGrant[] = [];
    selectedProductsOnCurrentPage = productsOfCurrentPage.value.filter((item: IProductVariantForGrant) =>
    (getSelectedProductsToAddFromStorage().includes(item.uuid) || getDatabaseSelectedProductOfCollectionFromStorage().includes(item.uuid))
    && !getUnSelectedProductsToRemoveFromStorage().includes(item.uuid));

    const totalUnSelectedProductsOnCurrentPage = getTotalUnSelectedProductsOnCurrentPage();
    isSelectAllBtnChecked.value = (itemsPerPage - totalUnSelectedProductsOnCurrentPage) === itemsPerPage;
    selectedProducts.value = selectedProductsOnCurrentPage;
}

async function populateDataTableWithLocalStorageFilters(itemsPerPage?: number, page?: number) {
  let localStorageRowPerPage = getLocalStorageRowsPerPage();
  let localStorageFisrtPageState = getLocalStorageFisrtPageState();
  let currentPage = page;
  if (!currentPage) {
    currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
  }
  let currentItemsPerPage = itemsPerPage;
  if (!currentItemsPerPage) {
    currentItemsPerPage = localStorageRowPerPage;
  }

  await populateDatatable(currentItemsPerPage as number, currentPage, criteria.value, selectedOptionsCategory.value);
}

async function onPaginate(e) { 
  rows.value = e.rows;
  page.value = e.page + 1;
  showSpinner();
  setLocalStorageRowsPerPage(rows.value);
  await populateDataTableWithLocalStorageFilters(rows.value, page.value);
}

const onRowSelect = async (event: any): Promise<void> => {
    const selectedProductUuid = event.data.uuid;
    removeSelectedProductInProductsToRemoveInStorage([selectedProductUuid]);
    if (!getDatabaseSelectedProductOfCollectionFromStorage().includes(selectedProductUuid)) {
        totalSelectedProductsToAdd.value++;
        addSelectedProductInProductsToAddInStorage([selectedProductUuid]);
    }

    const currentProductsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
    let newTotalProductsToRemoveInStorage = currentProductsToRemoveInStorage.filter(uuid => !selectedProductUuid.includes(uuid));
    totalUnSelectedProducts.value = newTotalProductsToRemoveInStorage.length;
    const totalSelectedProductsOnCurrentPage = getTotalUnSelectedProductsOnCurrentPage();
    isSelectAllBtnChecked.value = (refItemsPerPage.value - totalSelectedProductsOnCurrentPage) === refItemsPerPage.value;
}

const onRowUnselect = async (event: any): Promise<void> => {
    const unSelectedProductUuid = event.data.uuid;
    removeUnSelectedProductFromProductsToAddInStorage([unSelectedProductUuid]);
    if (getDatabaseSelectedProductOfCollectionFromStorage().includes(unSelectedProductUuid)) {
        addUnSelectedProductInProductsToRemoveInStorage([unSelectedProductUuid]);
        totalUnSelectedProducts.value = getUnSelectedProductsToRemoveFromStorage().length++;
    }

    const productsToAddInStorage = getSelectedProductsToAddFromStorage();
    let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !unSelectedProductUuid.includes(uuid));
    totalSelectedProductsToAdd.value = newProductsToAddInStorage.length;
    isSelectAllBtnChecked.value = false;
}

const onRowSelectAll = async (event): Promise<void> => {
    if (true === event.checked) {
        isSelectAllBtnChecked.value = true;
        selectedProducts.value = productsOfCurrentPage.value;
        const selectedProductsUuidsToRemoveInStorage = productsOfCurrentPage.value
            .filter((item: IProductVariantForGrant) =>
                getDatabaseSelectedProductOfCollectionFromStorage().includes(item.uuid) ||
                getUnSelectedProductsToRemoveFromStorage().includes(item.uuid)
            )
            .map(item => item.uuid);
        removeSelectedProductInProductsToRemoveInStorage(selectedProductsUuidsToRemoveInStorage);

        const selectedProductUuidToSaveInDatabase = productsOfCurrentPage.value
            .filter((item: IProductVariantForGrant) =>
                !getDatabaseSelectedProductOfCollectionFromStorage().includes(item.uuid)
            )
            .map(item => item.uuid);
        if (selectedProductUuidToSaveInDatabase.length > 0) {
            addSelectedProductInProductsToAddInStorage(selectedProductUuidToSaveInDatabase);
            const currentProductsToAddInStorage = getSelectedProductsToAddFromStorage();
            let newProductsToAddInStorage = currentProductsToAddInStorage;
            selectedProductUuidToSaveInDatabase.forEach((uuid: string) => {
                if (!currentProductsToAddInStorage.includes(uuid)) {
                    newProductsToAddInStorage.push(uuid);
                }
            });
            totalSelectedProductsToAdd.value = newProductsToAddInStorage.length;
        }

        const currentProductsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
        let newTotalProductsToRemoveInStorage = currentProductsToRemoveInStorage.filter(uuid => !selectedProductsUuidsToRemoveInStorage.includes(uuid));
        totalUnSelectedProducts.value = newTotalProductsToRemoveInStorage.length;
    } else {
        isSelectAllBtnChecked.value = false;
        const unSelectedProductsToRemoveInDatabase = productsOfCurrentPage.value
            .filter((item: IProductVariantForGrant) =>
                !getDatabaseSelectedProductOfCollectionFromStorage().includes(item.uuid) ||
                getSelectedProductsToAddFromStorage().includes(item.uuid)
            );
        const unSelectedProductsUuidsToRemoveInDatabase = unSelectedProductsToRemoveInDatabase.map(item => item.uuid);
        removeUnSelectedProductFromProductsToAddInStorage(unSelectedProductsUuidsToRemoveInDatabase);

        const unSelectedProductsToRemoveInStorage = productsOfCurrentPage.value
            .filter((item: IProductVariantForGrant) =>
                getDatabaseSelectedProductOfCollectionFromStorage().includes(item.uuid)
            );
        const unSelectedProductsUuidsToRemoveInStorage = unSelectedProductsToRemoveInStorage.map(item => item.uuid);
        if (unSelectedProductsUuidsToRemoveInStorage.length > 0) {
            addUnSelectedProductInProductsToRemoveInStorage(unSelectedProductsUuidsToRemoveInStorage);
            const productsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
            let newProductsToRemoveInStorage = productsToRemoveInStorage;
            unSelectedProductsUuidsToRemoveInStorage.forEach((uuid: string) => {
                if (!productsToRemoveInStorage.includes(uuid)) {
                    newProductsToRemoveInStorage.push(uuid);
                }
            });
            totalUnSelectedProducts.value = newProductsToRemoveInStorage.length;
        }

        const productsToAddInStorage = getSelectedProductsToAddFromStorage();
        let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !unSelectedProductsUuidsToRemoveInDatabase.includes(uuid));
        totalSelectedProductsToAdd.value = newProductsToAddInStorage.length;
        selectedProducts.value = selectedProducts.value.filter((item: IProductVariantForGrant) =>
            !productsOfCurrentPage.value.map((product: IProductVariantForGrant) => product.uuid).includes(item.uuid)
        );

        hideSpinner();
    }
}

const setIsSelectedAllBtnStatusLocalStorage = (status: boolean) =>{
    isSelectAllBtnChecked.value = status;

    const isSelectedAllBtnInStorageData = localStorage.getItem(selectAllBtnStatusStorageKey);
    if (!isSelectedAllBtnInStorageData) {
        localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
    } else {
        const currentStatus = JSON.parse(isSelectedAllBtnInStorageData);
        if (currentStatus !== status) {
            localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
        }
    }
}

function getLocalStorageData() {
  let storageData = localStorage.getItem(tableLocalStorageKey);

  if (null !== storageData && "undefined" !== storageData) {
    return JSON.parse(storageData);
  }

  return null;
}

const getTotalUnSelectedProductsOnCurrentPage = (): number => {
    const totalUnselectedCurrentPageProductsNotInDbAndAddFromStorage = productsOfCurrentPage.value.filter((item: IProductVariantForGrant) =>
        (!getSelectedProductsToAddFromStorage().includes(item.uuid) && !getDatabaseSelectedProductOfCollectionFromStorage().includes(item.uuid))).length;
    const totalUnselectedCurrentPageProductsOnStorage = productsOfCurrentPage.value.filter((item: IProductVariantForGrant) =>
        getUnSelectedProductsToRemoveFromStorage().includes(item.uuid)).length;

    return totalUnselectedCurrentPageProductsOnStorage + totalUnselectedCurrentPageProductsNotInDbAndAddFromStorage;
};

const getDatabaseSelectedProductOfCollectionFromStorage = (): string[] => {
    const initSelectedProductsOfCollection = localStorage.getItem(dbProductsOfCollectionInStorage);
    return initSelectedProductsOfCollection ? JSON.parse(initSelectedProductsOfCollection) : [];
}

const addDbProductsToCollectionInStorage = (UuidsCheckedInDb: string[]) => {
    let initProductsOfCollectionInStorage = getDatabaseSelectedProductOfCollectionFromStorage();
    UuidsCheckedInDb.forEach(uuid => {
        if (!initProductsOfCollectionInStorage.includes(uuid)) {
            initProductsOfCollectionInStorage.push(uuid);
        }
    });
    localStorage.setItem(dbProductsOfCollectionInStorage, JSON.stringify(initProductsOfCollectionInStorage));
}

const getSelectedProductsToAddFromStorage = (): string[] => {
    const selectedProductsToAdd = localStorage.getItem(selectedProductToAddStorageKey);
    return selectedProductsToAdd ? JSON.parse(selectedProductsToAdd) : [];
}

const addSelectedProductInProductsToAddInStorage = (uuids: string[]) => {
    if (uuids.length === 0) {
        totalSelectedProductsToAdd.value = 0;
        localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify([]));
        return;
    }

    let productsToAddInStorage = getSelectedProductsToAddFromStorage();
    uuids.forEach(uuid => {
        if (!productsToAddInStorage.includes(uuid)) {
            productsToAddInStorage.push(uuid);
        }
    });
    localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify(productsToAddInStorage));
}

const removeUnSelectedProductFromProductsToAddInStorage = (uuids: string[]) => {
    let productsToAddInStorage = getSelectedProductsToAddFromStorage();
    let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !uuids.includes(uuid));
    localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify(newProductsToAddInStorage));
}

const getUnSelectedProductsToRemoveFromStorage = (): string[] => {
    const unSelectedProductsToRemove = localStorage.getItem(selectedProductToRemoveStorageKey);
    return unSelectedProductsToRemove ? JSON.parse(unSelectedProductsToRemove) : [];
}

const addUnSelectedProductInProductsToRemoveInStorage = (uuids: string[]) => {
    if (uuids.length === 0) {
        totalUnSelectedProducts.value = 0;
        localStorage.setItem(selectedProductToRemoveStorageKey, JSON.stringify([]));
        return;
    }

    let productsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
    uuids.forEach(uuid => {
        if (!productsToRemoveInStorage.includes(uuid)) {
            productsToRemoveInStorage.push(uuid);
        }
    });
    localStorage.setItem(selectedProductToRemoveStorageKey, JSON.stringify(productsToRemoveInStorage));
}

const removeSelectedProductInProductsToRemoveInStorage = (uuidsToRemove: string[]) => {
    let currentProductsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
    let newProductsToRemoveInStorage = currentProductsToRemoveInStorage.filter(uuid => !uuidsToRemove.includes(uuid));
    localStorage.setItem(selectedProductToRemoveStorageKey, JSON.stringify(newProductsToRemoveInStorage));
}

function getSelectedProductUuids() {
  let localStorageData = getLocalStorageData();
  return localStorageData.selection.map(({ uuid }) => uuid)
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
  let storageData = localStorage.getItem(tableLocalStorageKey);
  if (null !== storageData) {
    let localStorageObject = JSON.parse(storageData);
    localStorageObject.rows = rowPerPage;
    localStorage.setItem(tableLocalStorageKey, JSON.stringify(localStorageObject));
  }
}

function getLocalStorageRowsPerPage() {
  let localStorageData = getLocalStorageData();
  if (null !== localStorageData && localStorageData.rows) {
    return localStorageData.rows;
  }

  return defaultRowsPerPage;
}

function getLocalStorageFisrtPageState() {
  let localStorageData = getLocalStorageData();
  if (null !== localStorageData && localStorageData.first) {
    return localStorageData.first;
  }

  return 0;
}

const initLocalStorageData = () => {
    // Delete
    if (localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.removeItem(selectAllBtnStatusStorageKey);
    }
    if (localStorage.getItem(tableLocalStorageKey)) {
        localStorage.removeItem(tableLocalStorageKey);
    }
    if (localStorage.getItem(selectedProductToAddStorageKey)) {
        localStorage.removeItem(selectedProductToAddStorageKey);
    }
    if (localStorage.getItem(selectedProductToRemoveStorageKey)) {
        localStorage.removeItem(selectedProductToRemoveStorageKey);
    }
    if (localStorage.getItem(dbProductsOfCollectionInStorage)) {
        localStorage.removeItem(dbProductsOfCollectionInStorage);
    }

    // Create
    if (!localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.setItem(selectAllBtnStatusStorageKey, 'false');
    }
    if (!localStorage.getItem(selectedProductToAddStorageKey)) {
        localStorage.setItem(selectedProductToAddStorageKey, '[]');
    }
    if (!localStorage.getItem(selectedProductToRemoveStorageKey)) {
        localStorage.setItem(selectedProductToRemoveStorageKey, '[]');
    }
    if (!localStorage.getItem(dbProductsOfCollectionInStorage)) {
        localStorage.setItem(dbProductsOfCollectionInStorage, '[]');
    }
}

const showSpinner = () => {
    isLoading.value = true;
}
const hideSpinner = () => {
    isLoading.value = false;
}
</script>